import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { operationalRouteTemplate } from '../../../analytics/operationalRuntime';
import {
  markProductNavigationReady,
  recordProductLandingReady,
  startProductNavigation,
} from '../../../analytics/productAnalyticsRuntime';
import { useAuth } from '../auth/authHooks';
import { routeNavigationEligibility } from './routeNavigationEligibility';

type NavigationEligibility = 'eligible' | 'redirect' | 'access_denied';

export function RouteHeadingFocus({
  analyticsEnabled = false,
  eligibilityCategory,
}: {
  analyticsEnabled?: boolean;
  eligibilityCategory?: NavigationEligibility;
}) {
  const location = useLocation();
  useEffect(() => {
    let settled = false;
    let observedHeading: HTMLElement | null = null;
    const routeTemplate = operationalRouteTemplate(location.pathname);
    const navigationId = analyticsEnabled
      ? (eligibilityCategory
        ? startProductNavigation(routeTemplate, eligibilityCategory)
        : startProductNavigation(routeTemplate))
      : null;
    const markSettled = () => {
      if (settled) return;
      settled = true;
      recordProductLandingReady(routeTemplate);
      markProductNavigationReady(navigationId, routeTemplate);
    };
    const observeHeadingFocus = () => {
      const heading = document.querySelector('h1');
      if (!(heading instanceof HTMLElement)) return null;
      if (observedHeading !== heading) {
        observedHeading?.removeEventListener('focus', markSettled);
        observedHeading = heading;
        observedHeading.addEventListener('focus', markSettled, { once: true });
      }
      return heading;
    };
    const focusHeading = () => {
      const heading = observeHeadingFocus();
      if (!heading) return false;
      if (document.activeElement === heading) {
        markSettled();
        return true;
      }
      const headingMain = heading.closest('main');
      if (headingMain?.contains(document.activeElement)) {
        markSettled();
        return true;
      }
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus();
      markSettled();
      return true;
    };
    observeHeadingFocus();
    const frame = window.requestAnimationFrame(() => {
      if (!settled) focusHeading();
    });
    const observer = new MutationObserver(() => {
      if (!settled && focusHeading()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timeout = window.setTimeout(() => observer.disconnect(), 5000);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      observer.disconnect();
      observedHeading?.removeEventListener('focus', markSettled);
    };
  }, [analyticsEnabled, eligibilityCategory, location.pathname]);
  return null;
}

export function AuthenticatedRouteHeadingFocus() {
  const { user } = useAuth();
  const location = useLocation();
  return (
    <RouteHeadingFocus
      analyticsEnabled={Boolean(user?.email)}
      eligibilityCategory={routeNavigationEligibility(location.pathname, user)}
    />
  );
}
